import React, { useState, useEffect, Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import Layout from "../../components/global/layout";

// ─── Main Component ───────────────────────────────────────────────
const QuoteList = () => {
  const [quotes, setQuotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const fetchQuotes = async () => {
    loader(true);
    await ApiClient.get("quote-request/listing")
      .then((res) => {
        if (res?.success) setQuotes(res?.data?.quotes || res?.data || []);
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => { fetchQuotes(); }, []);

  const openViewModal = async (id) => {
    loader(true);
    await ApiClient.get(`quote-request/detail?id=${id}`)
      .then((res) => {
        if (res?.success) {
          setSelectedQuote(res.data);
          setIsViewModalOpen(true);
        }
      })
      .catch(console.error);
    loader(false);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedQuote(null);
  };

  const handleDelete = (id) => {
    swal({
      text: "This quote request will be permanently deleted.",
      icon: "warning",
      className: "smallModal",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      await ApiClient.delete(`quote-request/delete?id=${id}`)
        .then(async (res) => {
          if (res?.success) {
            await fetchQuotes();
            swal({ text: "Quote deleted successfully.", icon: "success", className: "smallModal" });
          }
        })
        .catch(console.error);
      loader(false);
    });
  };

  const filtered = (quotes || []).filter((q) => {
    const s = searchQuery.toLowerCase();
    return (
      q.name?.toLowerCase().includes(s) ||
      q.email?.toLowerCase().includes(s) ||
      q.phoneNumber?.toString().toLowerCase().includes(s) ||
      q.comment?.toLowerCase().includes(s)
    );
  });

  // ── Format date ──
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // ── Format time ──
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  // ── Avatar initials ──
  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">

        {/* ─── Page Header ─── */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">Quote Requests</h2>
            <p className="text-sm sm:text-md text-gray-600">
              {quotes.length} quote request{quotes.length !== 1 ? "s" : ""} total
            </p>
          </div>
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">

          {/* ─── Search ─── */}
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            <div className="relative w-full sm:w-[350px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* ─── Table ─── */}
          <div className="overflow-auto scrollbar border border-gray-200 rounded-xl">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Customer", "Email", "Phone", "Comment", "Received", "Actions"].map((h) => (
                    <th key={h} className="text-left py-3 px-5">
                      <span className="text-sm text-gray-700 font-[400] whitespace-nowrap">{h}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                            strokeLinejoin="round" className="text-gray-600"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">No quote requests found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((item, index) => (
                    <tr key={item.id || index} className="hover:bg-gray-50/60 transition-colors">

                      {/* Customer */}
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-3">
                          <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-[#115B9D]">
                              {getInitials(item.name)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 whitespace-nowrap capitalize">{item.name || "—"}</p>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-700 whitespace-nowrap">{item.email || "—"}</p>
                      </td>

                      {/* Phone */}
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-700 whitespace-nowrap">{item.phoneNumber || "—"}</p>
                      </td>

                      {/* Comment */}
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-500 max-w-[220px] truncate">{item.comment || "—"}</p>
                      </td>

                      {/* Received Date */}
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-700 whitespace-nowrap">{formatDate(item.createdAt)}</p>
                        <p className="text-xs text-gray-600">{formatTime(item.createdAt)}</p>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-2">
                          {/* View */}
                          <button
                            type="button"
                            onClick={() => openViewModal(item?.id||item?._id)}
                            title="View"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => handleDelete(item?.id||item?._id)}
                            title="Delete"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              <line x1="10" x2="10" y1="11" y2="17" />
                              <line x1="14" x2="14" y1="11" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ─── View Quote Modal ─── */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeViewModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">

                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round" className="text-[#115B9D]">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Quote Request Details
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={closeViewModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  {selectedQuote && (
                    <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 scrollbar">

                      {/* ── Avatar + Name ── */}
                      <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                        <div className="size-14 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                          <span className="text-lg font-bold text-[#115B9D]">
                            {getInitials(selectedQuote.name)}
                          </span>
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900 capitalize">{selectedQuote.name || "—"}</p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Submitted on {formatDate(selectedQuote.createdAt)}{" "}
                            {formatTime(selectedQuote.createdAt) && `at ${formatTime(selectedQuote.createdAt)}`}
                          </p>
                        </div>
                      </div>

                      {/* ── Contact Info ── */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Contact Information
                        </p>
                        <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">

                          {/* Email */}
                          <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round" className="text-[#115B9D]">
                                <rect width="20" height="16" x="2" y="4" rx="2" />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">Email</p>
                              <p className="text-sm text-gray-800 break-all mt-0.5">{selectedQuote.email || "—"}</p>
                            </div>
                          </div>

                          {/* Phone */}
                          <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round" className="text-[#115B9D]">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">Phone</p>
                              <p className="text-sm text-gray-800 mt-0.5 break-words">{selectedQuote.phoneNumber || "—"}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── Comment ── */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">Message / Comment</p>
                        <div className="pt-2">
                          <div className="p-3 border border-gray-100 rounded-xl bg-gray-50/50 min-h-[80px]">
                            {selectedQuote.comment ? (
                              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                {selectedQuote.comment}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 italic">No comment provided.</p>
                            )}
                          </div>
                        </div>
                      </div>

                    </div>
                  )}

                  {/* Modal Footer */}
                  <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    <button
                      type="button"
                      onClick={closeViewModal}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                    >
                      Close
                    </button>
                    {selectedQuote && (
                      <button
                        type="button"
                        onClick={() => {
                          closeViewModal();
                          handleDelete(selectedQuote?.id||selectedQuote?._id);
                        }}
                        className="inline-flex items-center gap-2 bg-red-500 hover:opacity-80 text-white text-sm px-4 py-2.5 rounded-xl transition-colors font-medium"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                        Delete
                      </button>
                    )}
                  </div>

                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
};

export default QuoteList;